/**
 * SpanishFooter — ES mirror of the EN Footer. Same visual structure
 * (dark grid, awards, social, map, legal links) but native Spanish copy
 * and /es/* link targets.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ROUTE_MAP_EN_TO_ES } from '@/i18n/routeMap';

const r = ROUTE_MAP_EN_TO_ES;

const SpanishFooter = () => {
  return (
    <footer className="bg-gray-800 text-gray-300 py-12" data-testid="spanish-footer">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand block */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-teal-400">Fort Worth Dental</h3>
            <p className="text-gray-400 text-sm mb-4">
              Atención dental galardonada para Fort Worth y todo el área metropolitana de DFW desde 1990. Hablamos español.
            </p>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start">
                <span className="mr-2 text-teal-400">📍</span>
                <span>4620 Citylake Blvd W Fort Worth, TX 76132</span>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-teal-400">📞</span>
                <a href="tel:8179853337" className="hover:text-teal-400">(817) 985-3337</a>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-teal-400">🌐</span>
                <span>fortworthdental.com</span>
              </li>
            </ul>
          </div>

          {/* Quick links — ES */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-teal-400">Enlaces Rápidos</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/es" className="text-gray-400 hover:text-teal-400">Inicio</Link></li>
              <li><Link to={r['/about-us']} className="text-gray-400 hover:text-teal-400">Sobre Nosotros</Link></li>
              <li><Link to={r['/implant-center']} className="text-teal-400 hover:text-teal-300 font-semibold">⭐ Centro de Implantes</Link></li>
              <li><Link to={r['/first-fit-veneers']} className="text-teal-400 hover:text-teal-300 font-semibold">⭐ First Fit Carillas</Link></li>
              <li><Link to={r['/dental-implants']} className="text-gray-400 hover:text-teal-400">Implantes Dentales</Link></li>
              <li><Link to={r['/specials']} className="text-gray-400 hover:text-teal-400">Promociones</Link></li>
              <li><Link to={r['/form-finances']} className="text-gray-400 hover:text-teal-400">Formularios y Finanzas</Link></li>
              <li><Link to={r['/testimonials']} className="text-gray-400 hover:text-teal-400">Testimonios</Link></li>
              <li><Link to={r['/areas-we-serve']} className="text-gray-400 hover:text-teal-400">Áreas que Atendemos</Link></li>
              <li><Link to="/es/blog" className="text-gray-400 hover:text-teal-400">Noticias Dentales</Link></li>
              <li><Link to="/es/contacto" className="text-gray-400 hover:text-teal-400">Contacto</Link></li>
            </ul>
          </div>

          {/* Awards */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-teal-400">Reconocimientos</h3>
            <div className="space-y-2 text-sm text-gray-400 mb-4">
              <div className="flex items-center"><span className="mr-2">🏆</span><span>Mejor Consultorio Dental 2020</span></div>
              <div className="flex items-center"><span className="mr-2">🏆</span><span>Mejor Consultorio Dental 2021</span></div>
              <div className="flex items-center"><span className="mr-2">🏆</span><span>Mejor Consultorio Dental 2023</span></div>
              <div className="flex items-center"><span className="mr-2">🏆</span><span>Mejor Consultorio Dental 2025</span></div>
            </div>
            <div className="mt-4">
              <img src="/images/badges/best-of-fort-worth-2025.png" alt="Best of Fort Worth 2025" className="w-full h-auto max-w-[150px]" />
            </div>
          </div>

          {/* Social + Map */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-teal-400">Conéctate con Nosotros</h3>
            <p className="text-sm text-gray-400 mb-4">
              Síguenos para consejos dentales y noticias.
            </p>
            <div className="flex space-x-4 mb-4">
              <a href="https://www.facebook.com/FWDental" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400" aria-label="Facebook">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://x.com/FortWorthTeeth" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400" aria-label="X">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://www.youtube.com/channel/UCUFwAVnty9mZPxUYT9VLyIA" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400" aria-label="YouTube">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/fort-worth-dental/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400" aria-label="LinkedIn">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
            </div>
            <div className="bg-gray-700 rounded overflow-hidden h-40">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3357.024917804545!2d-97.43825968481577!3d32.72836098098776!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e6d3c5d5d5d5d%3A0x5d5d5d5d5d5d5d5d!2s4620%20Citylake%20Blvd%20W%2C%20Fort%20Worth%2C%20TX%2076132!5e0!3m2!1ses!2sus!4v1234567890123"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Mapa de Fort Worth Dental"
              />
            </div>
          </div>
        </div>

        {/* Legal */}
        <div className="border-t border-gray-700 mt-8 pt-6">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 mb-4">
            <Link to={r['/terms']} className="hover:text-teal-400">Términos y Condiciones</Link>
            <span>|</span>
            <Link to={r['/privacy']} className="hover:text-teal-400">Política de Privacidad</Link>
            <span>|</span>
            <Link to={r['/hipaa-npp']} className="hover:text-teal-400">HIPAA — Aviso de Privacidad</Link>
            <span>|</span>
            <Link to={r['/sitemap']} className="hover:text-teal-400">Mapa del Sitio</Link>
          </div>
        </div>

        <div className="text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Fort Worth Dental. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

export default SpanishFooter;
